import React, { useCallback, useEffect, useState } from 'react';
import * as yup from 'yup';

import Input from '../../../common2/Input';
import Locale from '../../../Translate/Locale';
import Link from '../../../common/Link/Link';
import ClaimStepClosableMessage from './ClaimStepClosableMessage';
import { useValidateStep } from './hooks/useValidateStep';
import { ClaimStepContainerProps } from './ClaimStepContainer';
import { ClaimContext, StepProps } from '../types';
import type { SearchShipmentResult } from '../../../claims-center/submit-claims/useSearchShipment';

import styles from './SearchClaimInfoStep.scss';

const schema = yup.object().shape({
  trackingCode: yup.string().required().label('Tracking ID'),
});

export type SearchClaimInfoStepProps = {
  claim: Pick<ClaimContext, 'trackingCode'>;
  as: React.ComponentType<ClaimStepContainerProps>;
  onResetClaim: () => void;
  onResetShipmentSearch: () => void;
  onLoadShipmentByTrackingId: (trackingId: string) => Promise<SearchShipmentResult>;
} & StepProps;

function SearchClaimInfoStep(props: SearchClaimInfoStepProps) {
  const {
    as: Container,
    onLoadShipmentByTrackingId,
    onResetShipmentSearch,
    onBack,
    onNext,
    onChange,
    onResetClaim,
    isDisabled,
    isSubmitting,
    nonClaimableMessage,
    claim: { trackingCode = '' },
    ...restProps
  } = props;

  const [isClaimSubmissionBlocked, setIsClaimSubmissionBlocked] = useState(true);
  const [isNextBtnDisabled, setIsNextBtnDisabled] = useState(false);

  const onInputChange = useCallback(
    (value: string, name: string) => {
      onChange({ [name]: value });
      setIsNextBtnDisabled(false);
    },
    [onChange]
  );

  const handleOnBack = useCallback(() => {
    onResetShipmentSearch();
    onBack && onBack();
  }, [onBack, onResetShipmentSearch]);

  const handleOnNext = useCallback(async () => {
    const { isClaimSubmissionBlocked } = await onLoadShipmentByTrackingId(trackingCode);

    if (isClaimSubmissionBlocked) {
      setIsNextBtnDisabled(true);
    }

    setIsClaimSubmissionBlocked(isClaimSubmissionBlocked);
  }, [trackingCode, onLoadShipmentByTrackingId]);

  const { onClickNext, errors } = useValidateStep(schema, { trackingCode }, {}, { onNext: handleOnNext });

  // TODO: we need to rewrite shipment fetching logic to not rely in delayed state updates
  useEffect(() => {
    if (!isClaimSubmissionBlocked && onNext) {
      onNext();
    }
  }, [isClaimSubmissionBlocked, onNext]);

  useEffect(() => {
    onResetClaim();
  }, []);

  return (
    <Container
      {...restProps}
      onNext={onClickNext}
      onBack={handleOnBack}
      isDisabled={isDisabled || isNextBtnDisabled}
      isSubmitting={isSubmitting}
    >
      {nonClaimableMessage && (
        <ClaimStepClosableMessage intent="danger">
          <Locale>{nonClaimableMessage}</Locale>
        </ClaimStepClosableMessage>
      )}
      <Input
        name="trackingCode"
        label="SHIPPING_INFO_TRACKING_ID"
        placeholder="Add tracking ID"
        isRequired
        value={trackingCode}
        error={errors.trackingCode}
        onChange={onInputChange}
        topRightElement={
          <Link to="/parcel-finder">
            <div className={styles.link}>
              <Locale>Don’t have the tracking ID?</Locale>
            </div>
          </Link>
        }
      />
    </Container>
  );
}

export default React.memo(SearchClaimInfoStep);
